import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { loadConnectAndInitialize } from '@stripe/connect-js'
import {
  sharedStripeConnectSessionSurface,
  useStripeConnectOrgSession,
} from '@/components/stripe-connect/StripeConnectOrgSessionProvider'
import { useTheme } from '@/hooks/useTheme'
import { getAdminFontSources } from '@/lib/admin-font'
import { registerActiveStripeConnectInstance } from '@/lib/stripe-connect-session'
import { useCreateStripeConnectAccountSessionMutation } from '@/lib/services/stripe-connect/stripe-connect.query'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { reportStripeConnectFailure } from './stripe-connect-telemetry'
import { buildStripeConnectAppearance } from './stripeConnectAppearance'

export const STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS = 3000
export const STRIPE_CONNECT_LOAD_ERROR_MESSAGE = 'Stripe couldn’t load. Try again in a moment.'

export function useStripeConnectManager({
  organizationId,
  accountContext = 'current',
  surface,
  maxHydrationMs = null,
  failOnHydrationTimeout = true,
  onSessionConflict,
  toastTitle = 'Unable to load Stripe tools',
  showLoadErrorToast = true,
  subtleBorder = false,
} = {}) {
  const orgSession = useStripeConnectOrgSession()
  // The non-current onboarding target must never share the current account's SDK instance.
  const sharedOrgSession = accountContext === 'current' ? orgSession : null
  const { theme } = useTheme()
  const createAccountSessionMutation = useCreateStripeConnectAccountSessionMutation({
    organizationId,
    surface,
    accountContext,
  })
  const hasSharedOrgSession = Boolean(sharedOrgSession)
  const ensureSharedConnectInstance = sharedOrgSession?.ensureConnectInstance
  const sharedConnectInstance = sharedOrgSession?.connectInstance
  const sharedConnectInstanceSessionSurface = sharedOrgSession?.connectInstanceSessionSurface
  const sharedResetVersion = sharedOrgSession?.resetVersion
  const sharedSessionSurface = hasSharedOrgSession
    ? sharedStripeConnectSessionSurface(surface)
    : null
  const sharedConnectInstanceMatchesSurface =
    Boolean(sharedConnectInstance) && sharedConnectInstanceSessionSurface === sharedSessionSurface
  const initialRenderAttempt = sharedConnectInstanceMatchesSurface ? 1 : 0
  const mutateAsyncRef = useRef(createAccountSessionMutation.mutateAsync)
  const onSessionConflictRef = useRef(onSessionConflict)
  const latestThemeRef = useRef(theme)
  const loadErrorShownRef = useRef(false)
  const hydrationFallbackTimeoutRef = useRef(null)
  const bootstrapGenerationRef = useRef(0)
  const renderAttemptRef = useRef(initialRenderAttempt)
  const loaderStartedAttemptRef = useRef(null)
  const reportedFailureAttemptRef = useRef(null)
  // Stripe can call onLoadError well after the florist leaves the page; those are not failures
  // anyone saw.
  const isMountedRef = useRef(true)
  const [localConnectInstance, setLocalConnectInstance] = useState(null)
  const [renderAttemptKey, setRenderAttemptKey] = useState(initialRenderAttempt)
  // The ref rejects stale callbacks synchronously; state re-arms the external timeout effect.
  const [loaderStartedRenderAttempt, setLoaderStartedRenderAttempt] = useState(null)
  const [settledRenderAttempt, setSettledRenderAttempt] = useState(null)
  const [hasSessionConflict, setHasSessionConflict] = useState(false)
  const [loadErrorMessage, setLoadErrorMessage] = useState(null)
  const connectInstance = sharedConnectInstanceMatchesSurface
    ? sharedConnectInstance
    : localConnectInstance
  const isEmbeddedSurfaceHydrating =
    Boolean(connectInstance) && settledRenderAttempt !== renderAttemptKey && !loadErrorMessage

  useLayoutEffect(() => {
    latestThemeRef.current = theme
  }, [theme])

  useLayoutEffect(() => {
    onSessionConflictRef.current = onSessionConflict
  }, [onSessionConflict])

  useEffect(() => {
    mutateAsyncRef.current = createAccountSessionMutation.mutateAsync
  }, [createAccountSessionMutation.mutateAsync])

  const clearHydrationFallbackTimer = useCallback(() => {
    if (hydrationFallbackTimeoutRef.current) {
      window.clearTimeout(hydrationFallbackTimeoutRef.current)
      hydrationFallbackTimeoutRef.current = null
    }
  }, [])

  const beginRenderAttempt = useCallback(() => {
    const nextRenderAttempt = renderAttemptRef.current + 1
    renderAttemptRef.current = nextRenderAttempt
    loaderStartedAttemptRef.current = null
    reportedFailureAttemptRef.current = null
    loadErrorShownRef.current = false
    clearHydrationFallbackTimer()
    setRenderAttemptKey(nextRenderAttempt)
    setLoaderStartedRenderAttempt(null)
    setHasSessionConflict(false)
    setLoadErrorMessage(null)

    return nextRenderAttempt
  }, [clearHydrationFallbackTimer])

  const reportFailureOnce = useCallback(
    (error, { phase, elementTagName = null, loaderStarted = false } = {}) => {
      const renderAttempt = renderAttemptRef.current

      if (!isMountedRef.current || reportedFailureAttemptRef.current === renderAttempt) {
        return
      }

      reportedFailureAttemptRef.current = renderAttempt
      reportStripeConnectFailure({
        error,
        phase,
        surface,
        organizationId,
        elementTagName,
        loaderStarted,
      })
    },
    [organizationId, surface]
  )

  const showLoadError = useCallback(() => {
    setLoadErrorMessage(STRIPE_CONNECT_LOAD_ERROR_MESSAGE)

    if (!showLoadErrorToast || loadErrorShownRef.current) {
      return
    }

    loadErrorShownRef.current = true
    toast.error(toastTitle, {
      description: STRIPE_CONNECT_LOAD_ERROR_MESSAGE,
    })
  }, [showLoadErrorToast, toastTitle])

  const handleSessionRequestError = useCallback(
    (error) => {
      const payload = error?.response?.data

      if (error?.response?.status === 409 && payload?.onboarding_required) {
        setHasSessionConflict(true)
        onSessionConflictRef.current?.(payload)
        return true
      }

      const loaderStarted = loaderStartedAttemptRef.current === renderAttemptRef.current
      // The optional banner retries on its next mount. A request that never got a response
      // (offline, or Mobile Safari aborting it during navigation) is not worth an issue there.
      const isUnansweredBannerRequest =
        surface === 'banner' && isAxiosError(error) && !error.response

      if (!isUnansweredBannerRequest) {
        reportFailureOnce(error, { phase: 'account_session', loaderStarted })
      }

      if (!loaderStarted) {
        showLoadError()
      }

      return false
    },
    [reportFailureOnce, showLoadError, surface]
  )

  const requestAccountSession = useCallback(async () => mutateAsyncRef.current(), [])

  const bootstrapConnect = useCallback(async () => {
    const bootstrapGeneration = bootstrapGenerationRef.current + 1
    bootstrapGenerationRef.current = bootstrapGeneration

    if (!sharedConnectInstanceMatchesSurface) {
      beginRenderAttempt()
      setLocalConnectInstance(null)
    }

    if (ensureSharedConnectInstance) {
      try {
        await ensureSharedConnectInstance({
          surface,
          handleSessionRequestError,
          subtleBorder,
        })
      } catch (error) {
        if (bootstrapGenerationRef.current === bootstrapGeneration) {
          handleSessionRequestError(error)
        }
      }

      return
    }

    try {
      const session = await requestAccountSession()

      if (bootstrapGenerationRef.current !== bootstrapGeneration) {
        return
      }

      let initialClientSecret = session.client_secret
      const fetchClientSecret = async () => {
        const ownerIsCurrent = () => bootstrapGenerationRef.current === bootstrapGeneration

        if (!ownerIsCurrent()) {
          throw new Error('This Stripe Connect instance is no longer active.')
        }

        if (initialClientSecret) {
          const clientSecret = initialClientSecret
          initialClientSecret = null

          return clientSecret
        }

        try {
          const nextSession = await requestAccountSession()

          if (!ownerIsCurrent()) {
            throw new Error('This Stripe Connect instance is no longer active.')
          }
          if (nextSession.account_id !== session.account_id) {
            throw new Error('The payment account changed. Reopen these Stripe tools.')
          }

          return nextSession.client_secret
        } catch (error) {
          if (ownerIsCurrent()) {
            handleSessionRequestError(error)
          }

          throw error
        }
      }
      const nextConnectInstance = loadConnectAndInitialize({
        publishableKey: session.publishable_key,
        fetchClientSecret,
        appearance: buildStripeConnectAppearance(latestThemeRef.current, { subtleBorder }),
        fonts: getAdminFontSources(),
        locale: 'en-US',
      })

      if (bootstrapGenerationRef.current !== bootstrapGeneration) {
        return
      }

      setLocalConnectInstance(nextConnectInstance)
    } catch (error) {
      if (bootstrapGenerationRef.current === bootstrapGeneration) {
        handleSessionRequestError(error)
      }
    }
  }, [
    beginRenderAttempt,
    ensureSharedConnectInstance,
    handleSessionRequestError,
    requestAccountSession,
    sharedConnectInstanceMatchesSurface,
    subtleBorder,
    surface,
  ])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    void bootstrapConnect()

    return () => {
      bootstrapGenerationRef.current += 1
    }
  }, [bootstrapConnect, organizationId, accountContext, sharedResetVersion])

  useEffect(() => {
    // Local instances have no shared provider to end their authenticated Stripe session.
    if (!localConnectInstance) return
    const unregister = registerActiveStripeConnectInstance({
      organizationId,
      connectInstance: localConnectInstance,
      accountContext: `${accountContext}:${surface}`,
    })
    return () => {
      unregister?.()
      void localConnectInstance?.logout?.()?.catch(() => undefined)
    }
  }, [localConnectInstance, organizationId, accountContext, surface])

  const retryLoad = useCallback(() => {
    if (connectInstance) {
      beginRenderAttempt()
      return
    }

    void bootstrapConnect()
  }, [beginRenderAttempt, bootstrapConnect, connectInstance])

  useEffect(() => {
    if (!localConnectInstance || hasSharedOrgSession) {
      return
    }

    localConnectInstance.update({
      appearance: buildStripeConnectAppearance(theme, { subtleBorder }),
      locale: 'en-US',
    })
  }, [hasSharedOrgSession, localConnectInstance, subtleBorder, theme])

  useEffect(() => {
    clearHydrationFallbackTimer()

    if (!isEmbeddedSurfaceHydrating || !maxHydrationMs) {
      return
    }

    const ownedRenderAttempt = renderAttemptKey
    const expectsLoaderStart = loaderStartedRenderAttempt === ownedRenderAttempt
    // Loader start gets a fresh, longer window for Stripe's iframe geometry to settle.
    const hydrationTimeoutMs = expectsLoaderStart ? maxHydrationMs * 2 : maxHydrationMs
    const hydrationFallbackTimeoutId = window.setTimeout(() => {
      if (
        renderAttemptRef.current === ownedRenderAttempt &&
        (loaderStartedAttemptRef.current === ownedRenderAttempt) === expectsLoaderStart
      ) {
        if (!failOnHydrationTimeout) {
          // Optional surfaces may load after the skeleton budget. Keep their SDK
          // component mounted so real readiness/error callbacks can still arrive.
          setSettledRenderAttempt(ownedRenderAttempt)
          hydrationFallbackTimeoutRef.current = null
          return
        }
        const timeoutError = Object.assign(
          new Error(
            expectsLoaderStart
              ? 'Stripe embedded component started but did not finish rendering.'
              : 'Stripe embedded component did not start rendering.'
          ),
          { type: 'render_error' }
        )
        reportFailureOnce(timeoutError, {
          phase: 'embedded_component_timeout',
          loaderStarted: expectsLoaderStart,
        })
        showLoadError()
      }
      hydrationFallbackTimeoutRef.current = null
    }, hydrationTimeoutMs)

    hydrationFallbackTimeoutRef.current = hydrationFallbackTimeoutId

    return () => {
      window.clearTimeout(hydrationFallbackTimeoutId)

      if (hydrationFallbackTimeoutRef.current === hydrationFallbackTimeoutId) {
        hydrationFallbackTimeoutRef.current = null
      }
    }
  }, [
    clearHydrationFallbackTimer,
    failOnHydrationTimeout,
    isEmbeddedSurfaceHydrating,
    loaderStartedRenderAttempt,
    maxHydrationMs,
    renderAttemptKey,
    reportFailureOnce,
    showLoadError,
  ])

  useEffect(() => () => clearHydrationFallbackTimer(), [clearHydrationFallbackTimer])

  const ownedRenderAttempt = renderAttemptKey
  const handleLoaderStart = useCallback(() => {
    if (renderAttemptRef.current !== ownedRenderAttempt) {
      return
    }

    if (loaderStartedAttemptRef.current === ownedRenderAttempt) {
      return
    }

    loaderStartedAttemptRef.current = ownedRenderAttempt
    clearHydrationFallbackTimer()
    setLoaderStartedRenderAttempt(ownedRenderAttempt)
  }, [clearHydrationFallbackTimer, ownedRenderAttempt])

  const handleSurfacePainted = useCallback(() => {
    if (renderAttemptRef.current !== ownedRenderAttempt) {
      return
    }

    clearHydrationFallbackTimer()
    setSettledRenderAttempt(ownedRenderAttempt)
  }, [clearHydrationFallbackTimer, ownedRenderAttempt])

  const handleLoadError = useCallback(
    ({ error, elementTagName } = {}) => {
      if (!isMountedRef.current || renderAttemptRef.current !== ownedRenderAttempt) {
        return
      }

      const loaderStarted = loaderStartedAttemptRef.current === ownedRenderAttempt
      reportFailureOnce(error, {
        phase: 'embedded_component',
        elementTagName,
        loaderStarted,
      })

      if (loaderStarted) {
        clearHydrationFallbackTimer()
        setSettledRenderAttempt(ownedRenderAttempt)
        return
      }

      showLoadError()
    },
    [clearHydrationFallbackTimer, ownedRenderAttempt, reportFailureOnce, showLoadError]
  )

  return {
    connectInstance,
    isEmbeddedSurfaceHydrating,
    isLoading: !connectInstance && !loadErrorMessage && !hasSessionConflict,
    loadErrorMessage,
    handleLoadError,
    handleLoaderStart,
    handleSurfacePainted,
    renderAttemptKey,
    retryLoad,
  }
}
